import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { getIn, useFormikContext } from "formik";
import { FieldLabel, RemoteSelectField, http } from "react-invenio-forms";

const getVocabularyLabel = (item) =>
  item?.title_l10n ||
  item?.title?.en ||
  item?.title ||
  item?.label ||
  item?.id ||
  "";

const serializeVocabularySuggestions = (suggestions) => {
  const arr = Array.isArray(suggestions) ? suggestions : [suggestions];

  return arr.filter(Boolean).map((item) => ({
    key: item.id,
    value: item.id,
    text: getVocabularyLabel(item),
    item,
  }));
};

const stripQueryString = (url) => (url ? url.split("?")[0] : url);

const normalizeSchemaValue = (value) =>
  typeof value === "string" ? value.split("?")[0] : "";

const fetchVocabularyById = async ({
  vocabularyId,
  autocompleteFrom,
  autocompleteFromAcceptHeader,
  value,
}) => {
  if (!value) return null;

  const baseUrl =
    stripQueryString(autocompleteFrom) || `/api/vocabularies/${vocabularyId}`;

  const headers = {};
  if (autocompleteFromAcceptHeader) {
    headers.Accept = autocompleteFromAcceptHeader;
  }

  const response = await http.get(`${baseUrl}/${encodeURIComponent(value)}`, {
    headers,
  });

  return response?.data || null;
};

const normalizeVocabularyObject = ({
  item,
  fallbackItem,
  selectedId,
  property,
}) => {
  const source = item || fallbackItem || {};
  const id = source?.id || selectedId || "";

  return {
    id,
    schema: normalizeSchemaValue(
      source?.schema ||
        source?.vocabulary_id ||
        property?.schema_value ||
        property?.vocabulary_id ||
        ""
    ),
    url:
      source?.url ||
      source?.uri ||
      source?.props?.url ||
      source?.props?.uri ||
      source?.links?.self ||
      "",
    label: getVocabularyLabel(source) || getVocabularyLabel(fallbackItem) || id,
  };
};

export const NestedVocabularyField = ({
  fieldPath,
  property,
  label,
  required,
  onSelectedItem,
}) => {
  const { values, setFieldValue } = useFormikContext();

  // Stable ref so async callbacks always read the latest form values without
  // being added to useCallback dependency arrays (which would cause re-fetches).
  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const isVocabularyObject = property?.type === "vocabulary_object";

  // RemoteSelectField wants a scalar field value.
  // For vocabulary_object, keep the real object at `fieldPath`,
  // and use a sibling internal scalar field for the dropdown state.
  const selectFieldPath = isVocabularyObject
    ? `${fieldPath}__autocomplete_id`
    : fieldPath;

  const rawStoredValue = getIn(values, fieldPath, "");

  const storedValue =
    rawStoredValue && typeof rawStoredValue === "object"
      ? rawStoredValue.id || ""
      : rawStoredValue || "";

  const [initialSuggestions, setInitialSuggestions] = useState(() =>
    rawStoredValue && typeof rawStoredValue === "object" && rawStoredValue.id
      ? serializeVocabularySuggestions([rawStoredValue])
      : []
  );

  const autocompleteFrom = useMemo(
    () =>
      property?.autocompleteFrom ||
      property?.ui_props?.autocompleteFrom ||
      `/api/vocabularies/${property?.vocabulary_id}`,
    [property]
  );

  const autocompleteFromAcceptHeader = useMemo(
    () =>
      property?.autocompleteFromAcceptHeader ||
      property?.ui_props?.autocompleteFromAcceptHeader ||
      "application/vnd.inveniordm.v1+json",
    [property]
  );

  const clearable =
    property?.clearable ?? property?.ui_props?.clearable ?? true;

  const placeholder =
    property?.placeholder ||
    property?.ui_props?.placeholder ||
    `Search ${label}`;

  const loadSelectedValue = useCallback(async () => {
    if (!storedValue) {
      setInitialSuggestions([]);

      if (isVocabularyObject) {
        setFieldValue(selectFieldPath, "", false);
      }

      return;
    }

    // Pre-initialize selectFieldPath synchronously before the async fetch so
    // the dropdown shows the stored selection immediately without waiting for
    // the API roundtrip. Avoids the empty-field flash on draft reload.
    if (isVocabularyObject) {
      setFieldValue(selectFieldPath, storedValue, false);
    }

    try {
      const item = await fetchVocabularyById({
        vocabularyId: property?.vocabulary_id,
        autocompleteFrom,
        autocompleteFromAcceptHeader,
        value: storedValue,
      });

      setInitialSuggestions(item ? [item] : []);

      // If the stored vocabulary_object has no URL (e.g. old record stored before
      // URL resolution was added, or vocabulary item had no URI at selection time),
      // resolve it now from the freshly fetched item and silently patch the field.
      if (isVocabularyObject && item) {
        const currentObj = getIn(valuesRef.current, fieldPath, null);
        if (currentObj && typeof currentObj === "object" && !currentObj.url) {
          const resolved = normalizeVocabularyObject({
            item,
            selectedId: storedValue,
            property,
          });
          if (resolved.url) {
            setFieldValue(fieldPath, resolved, false);
          }
        }
      }
    } catch (error) {
      console.error(
        `Failed to resolve selected vocabulary for ${fieldPath}:`,
        error
      );
      console.error("Request URL:", error?.config?.url);
      console.error("Response data:", error?.response?.data);
      setInitialSuggestions([]);
    }
  }, [
    storedValue,
    isVocabularyObject,
    selectFieldPath,
    fieldPath,
    setFieldValue,
    property,
    autocompleteFrom,
    autocompleteFromAcceptHeader,
  ]);

  useEffect(() => {
    loadSelectedValue();
  }, [loadSelectedValue]);

  return (
    <RemoteSelectField
      key={`${selectFieldPath}:${autocompleteFrom}`}
      fieldPath={selectFieldPath}
      label={<FieldLabel htmlFor={selectFieldPath} label={label} />}
      required={required}
      multiple={false}
      clearable={clearable}
      placeholder={placeholder}
      noQueryMessage={placeholder}
      suggestionAPIUrl={autocompleteFrom}
      suggestionAPIHeaders={{
        Accept: autocompleteFromAcceptHeader,
      }}
      searchQueryParamName="suggest"
      searchOnFocus
      serializeSuggestions={serializeVocabularySuggestions}
      initialSuggestions={initialSuggestions}
      onValueChange={async ({ data, formikProps }, selectedSuggestions) => {
        const selected = selectedSuggestions?.[0] || null;
        const selectedId = data?.value || "";

        if (!selectedId) {
          formikProps.form.setFieldValue(selectFieldPath, "");

          if (isVocabularyObject) {
            formikProps.form.setFieldValue(fieldPath, null);
          } else {
            formikProps.form.setFieldValue(fieldPath, "");
          }

          if (onSelectedItem) {
            onSelectedItem(null);
          }

          setInitialSuggestions([]);
          return;
        }

        // Normal vocabulary: keep old id-only behavior.
        if (!isVocabularyObject) {
          formikProps.form.setFieldValue(fieldPath, selectedId);

          if (onSelectedItem) {
            onSelectedItem(selected?.item || null);
          }

          setInitialSuggestions(selected?.item ? [selected.item] : []);
          return;
        }

        // Vocabulary object: dereference selected ID and store full object.
        let item = null;

        try {
          item = await fetchVocabularyById({
            vocabularyId: property?.vocabulary_id,
            autocompleteFrom,
            autocompleteFromAcceptHeader,
            value: selectedId,
          });
        } catch (error) {
          console.error(
            `Failed to fetch vocabulary object for ${fieldPath}:`,
            error
          );
          item = null;
        }

        const vocabularyObject = normalizeVocabularyObject({
          item,
          fallbackItem: selected?.item,
          selectedId,
          property,
        });

        formikProps.form.setFieldValue(selectFieldPath, selectedId);
        formikProps.form.setFieldValue(fieldPath, vocabularyObject);

        if (onSelectedItem) {
          onSelectedItem(item || selected?.item || null);
        }

        setInitialSuggestions(
          item ? [item] : selected?.item ? [selected.item] : []
        );
      }}
    />
  );
};

NestedVocabularyField.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  property: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  onSelectedItem: PropTypes.func,
};

NestedVocabularyField.defaultProps = {
  required: false,
  onSelectedItem: undefined,
};

export default NestedVocabularyField;
